import { Link, useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@/components/Icon';
import { Spinner } from '@/components/Spinner';
import { useAsyncData } from '@/hooks/useAsyncData';
import { formatDateTime } from '@/lib/clientFormat';
import { fetchSupportTicket } from '@/api/support';

/**
 * Страница обращения: заголовок, описание целиком и вложения. В старой
 * админке этого экрана не было — описание обрезалось в ячейке таблицы, а
 * файлы показывались ссылками в той же строке.
 */
export function SupportTicketPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: ticket, error } = useAsyncData(
    id ? () => fetchSupportTicket(Number(id)) : null,
    [id],
    'Не удалось загрузить обращение.',
  );

  if (error) {
    return (
      <section className="catalog">
        <p className="field-error">{error}</p>
        <button type="button" className="btn btn--secondary" onClick={() => navigate('/support')}>
          К списку обращений
        </button>
      </section>
    );
  }

  if (!ticket) {
    return (
      <section className="catalog">
        <div className="panel-loading">
          <Spinner />
        </div>
      </section>
    );
  }

  return (
    <section className="catalog">
      <header className="catalog__header">
        <button type="button" className="ticket__back" onClick={() => navigate('/support')}>
          <Icon name="chevronLeft" size={15} /> Обращения в поддержку
        </button>
        <h1>{ticket.title ?? 'Без темы'}</h1>
        <p className="catalog__lede">
          {ticket.clientId ? (
            <Link to={`/clients/${ticket.clientId}`}>{ticket.client ?? 'Без клиента'}</Link>
          ) : (
            (ticket.client ?? 'Без клиента')
          )}
          {' · '}
          {formatDateTime(ticket.createdAt)}
        </p>
      </header>

      <div className="card">
        <div className="ticket__meta">
          <div>
            <span className="ticket__meta-label">Автор</span>
            <span className="ticket__meta-value">{ticket.authorName ?? '—'}</span>
          </div>
          <div>
            <span className="ticket__meta-label">E-mail</span>
            <span className="ticket__meta-value">{ticket.authorEmail ?? '—'}</span>
          </div>
        </div>

        <h2 className="detail__subtitle">Описание</h2>
        {ticket.description ? (
          /* Описание — HTML из визуального редактора кабинета клиента (с
             картинками в data:-URI). Это чужой ввод, поэтому сервер уже
             вырезал скрипты и обработчики событий — см. sanitizeHtml()
             в SupportController. */
          <div
            className="ticket__description rich-text-preview"
            dangerouslySetInnerHTML={{ __html: ticket.description }}
          />
        ) : (
          <p className="cell-muted">Описание не указано.</p>
        )}

        <h2 className="detail__subtitle">Прикреплённые файлы</h2>
        {ticket.files.length === 0 ? (
          <p className="cell-muted">Файлов нет.</p>
        ) : (
          <ul className="ticket__files">
            {ticket.files.map((file) => (
              <li key={file.id}>
                <a href={file.url} target="_blank" rel="noreferrer" className="ticket__file">
                  <Icon name="fileText" size={16} />
                  <span className="ticket__file-name">{file.name}</span>
                  <span className="ticket__file-size">{formatFileSize(file.size)}</span>
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function formatFileSize(bytes: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}
